import { useState } from "react";
import { openPdf } from "@/lib/print";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Download, Loader2, AlertTriangle, CheckCircle2, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import AccountLedgerDrawer from "@/pages/reports/AccountLedgerDrawer";
import { ACCOUNT_NOTES } from "@/pages/reports/accountNotes";
import { HideWhenSingleCompany } from "@/components/CompanyField";

/** 1,23,456.00 — negatives in brackets, the way accountants read them. */
const fmtMoney = (v) => {
  const n = Number(v || 0);
  const s = Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `(${s})` : s;
};

/** An amount; when it belongs to an account it is a button that opens the entries behind it. */
function Amt({ value, row, drill, strong }) {
  const n = Number(value || 0);
  const tone = n < 0 ? "text-rose-500" : "text-foreground";
  const base = `font-mono tabular-nums ${tone} ${strong ? "font-semibold" : ""}`;
  if (row && row.account_id && drill && !row.is_group) {
    return (
      <button type="button" onClick={() => drill(row)} title="Click to see the entries behind this amount"
        className={`${base} rounded px-1 underline decoration-dotted decoration-muted-foreground/60 underline-offset-4 hover:bg-muted hover:decoration-solid`}
        data-testid={`amount-${row.account_code}`}>
        {fmtMoney(n)}
      </button>
    );
  }
  return <span className={base}>{fmtMoney(n)}</span>;
}

/** Account meaning + unusual-balance flag, shown under the account name. */
function AccountNote({ row, natural, explain }) {
  const meta = ACCOUNT_NOTES[row.account_code];
  if (row.is_group) return null;
  const amount = Number(row.amount ?? 0);
  let unusual = false;
  if (natural && meta && meta.normal !== "either" && amount !== 0) {
    const expectedOpposite = meta.normal !== natural;
    unusual = expectedOpposite ? amount > 0 : amount < 0;
  } else if (natural && !meta && amount < 0) unusual = true;
  if (!explain && !unusual) return null;
  return (
    <div className="mt-0.5 text-[11px] font-normal leading-snug text-muted-foreground">
      {unusual && <span className="mr-1 inline-flex items-center gap-1 font-medium text-amber-600 dark:text-amber-400"><AlertTriangle className="h-3 w-3" />Balance is on the unusual side — please check.</span>}
      {explain && meta?.note}
    </div>
  );
}

const TABS = [
  { key: "trial_balance", label: "Trial Balance" },
  { key: "profit_and_loss", label: "Profit & Loss" },
  { key: "balance_sheet", label: "Balance Sheet" },
  { key: "cash_bank_summary", label: "Cash & Bank" },
  { key: "cash_flow", label: "Cash Flow" },
];
const RANGE_TABS = ["profit_and_loss", "cash_flow"];

/** Insert group headings (with subtotals) above the accounts that belong to them. Top-level groups are skipped. */
function withGroups(rows, accounts, keys) {
  if (!accounts) return rows.map((r) => ({ ...r, level: 0 }));
  const byCode = Object.fromEntries(accounts.map((a) => [a.code, a]));
  const byId = Object.fromEntries(accounts.map((a) => [a.id, a]));
  const chainOf = (acc) => {
    const out = [];
    let p = acc?.parent_id ? byId[acc.parent_id] : null;
    while (p) { if (p.parent_id) out.unshift(p); p = p.parent_id ? byId[p.parent_id] : null; }
    return out;
  };
  const totals = {};
  const withChain = rows.map((r) => ({ r, chain: chainOf(byCode[r.account_code]) }));
  withChain.forEach(({ r, chain }) => chain.forEach((g) => {
    if (!totals[g.id]) totals[g.id] = Object.fromEntries(keys.map((k) => [k, 0]));
    keys.forEach((k) => { totals[g.id][k] += Number(r[k] || 0); });
  }));
  const path = ({ r, chain }) => [...chain.map((g) => g.code), r.account_code].join("/");
  withChain.sort((a, b) => path(a).localeCompare(path(b), undefined, { numeric: true }));
  const out = [];
  const shown = new Set();
  withChain.forEach(({ r, chain }) => {
    chain.forEach((g, i) => {
      if (shown.has(g.id)) return;
      shown.add(g.id);
      out.push({ account_code: g.code, account_name: g.name, is_group: true, level: i, ...totals[g.id] });
    });
    out.push({ ...r, level: chain.length });
  });
  return out;
}

export default function FinancialStatements() {
  const { companies } = useCompany() || {};
  const [tab, setTab] = useState("trial_balance");
  const [companyId, setCompanyId] = useState(companies?.[0]?.id || "");
  const [asOf, setAsOf] = useState(todayISO());
  const [dateFrom, setDateFrom] = useState(todayISO().slice(0, 8) + "01");
  const [dateTo, setDateTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [grouped, setGrouped] = useState(false);
  const [accounts, setAccounts] = useState(null);
  const [explain, setExplain] = useState(true);
  const [drillTarget, setDrillTarget] = useState(null);
  const [loaded, setLoaded] = useState({ from: null, to: null });

  const toggleGrouped = async (v) => {
    setGrouped(v);
    if (v && !accounts) {
      try { const { data } = await api.get("/gl/accounts"); setAccounts(data); } catch (err) { toast.error(getErrorMessage(err)); }
    }
  };
  const groupAccounts = grouped ? accounts : null;

  const effectiveCompanyId = companyId || companies?.[0]?.id;

  const load = async (o = {}) => {
    if (!effectiveCompanyId) return;
    const t = o.tab || tab, from = o.from || dateFrom, to = o.to || dateTo;
    setLoading(true);
    try {
      const endpoint = { trial_balance: "trial-balance", profit_and_loss: "profit-and-loss", balance_sheet: "balance-sheet", cash_bank_summary: "cash-bank-summary", cash_flow: "cash-flow" }[t];
      const params = RANGE_TABS.includes(t) ? { company_id: effectiveCompanyId, date_from: from, date_to: to } : { company_id: effectiveCompanyId, as_of: asOf || undefined };
      const { data } = await api.get(`/reports/${endpoint}`, { params });
      setLoaded(RANGE_TABS.includes(t) ? { from, to } : { from: null, to: asOf });
      setData(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    setExporting(true);
    try {
      const response = await api.post(
        "/reports/export",
        { report_key: tab === "cash_bank_summary" ? "cash_bank_summary" : tab, format, company_id: effectiveCompanyId, as_of: asOf || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tab}.${format === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  const drill = (row) => setDrillTarget({ accountId: row.account_id, code: row.account_code, name: row.account_name, dateFrom: loaded.from, dateTo: loaded.to });
  const openProfit = (from, to) => {
    setTab("profit_and_loss"); setDateFrom(from); setDateTo(to); setData(null);
    load({ tab: "profit_and_loss", from, to });
  };

  return (
    <div className="space-y-6" data-testid="financial-statements-page">
      <PageHeader
        title="Financial Statements"
        description="Trial Balance, Profit & Loss, Balance Sheet and Cash & Bank — per company, as of a date."
        action={
          <div className="flex items-center gap-2">
            <HideWhenSingleCompany><Select value={companyId || companies?.[0]?.id || ""} onValueChange={setCompanyId}>
              <SelectTrigger className="w-48" data-testid="financials-company-select"><SelectValue placeholder="Company" /></SelectTrigger>
              <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select></HideWhenSingleCompany>
            {RANGE_TABS.includes(tab) ? (
              <>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" data-testid="financials-date-from-input" />
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" data-testid="financials-date-to-input" />
              </>
            ) : (
              <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-36" data-testid="financials-as-of-input" />
            )}
            <Button onClick={() => load()} disabled={loading} data-testid="financials-run-button">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run"}</Button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <Button key={t.key} size="sm" variant={tab === t.key ? "default" : "outline"} onClick={() => { setTab(t.key); setData(null); }} data-testid={`tab-${t.key}-button`}>
            {t.label}
          </Button>
        ))}
        {["trial_balance", "profit_and_loss", "balance_sheet"].includes(tab) && (
          <label className="ml-2 flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={grouped} onCheckedChange={toggleGrouped} data-testid="financials-grouped-switch" /> Show groups
          </label>
        )}
        {["trial_balance", "profit_and_loss", "balance_sheet", "cash_bank_summary"].includes(tab) && (
          <label className="ml-2 flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={explain} onCheckedChange={setExplain} data-testid="financials-explain-switch" /> Explain accounts
          </label>
        )}
        {data && tab === "cash_flow" && (
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => openPdf("/reports/cash-flow/pdf", { company_id: effectiveCompanyId, date_from: dateFrom, date_to: dateTo })} data-testid="cash-flow-pdf-button">
            <Download className="h-3.5 w-3.5" /> PDF
          </Button>
        )}
        {data && !["cash_bank_summary", "cash_flow"].includes(tab) && (
          <div className="ml-auto flex gap-1.5">
            <Button size="sm" variant="outline" disabled={exporting} onClick={() => exportReport("excel")} data-testid="export-excel-button"><Download className="h-3.5 w-3.5" /> Excel</Button>
            <Button size="sm" variant="outline" disabled={exporting} onClick={() => exportReport("pdf")} data-testid="export-pdf-button"><Download className="h-3.5 w-3.5" /> PDF</Button>
          </div>
        )}
      </div>

      {!data ? (
        <EmptyState title="Run a statement" description="Pick a company and date, then click Run." testId="financials-empty-state" />
      ) : tab === "trial_balance" ? (
        <TrialBalanceView data={data} accounts={groupAccounts} drill={drill} explain={explain} />
      ) : tab === "profit_and_loss" ? (
        <ProfitAndLossView data={data} accounts={groupAccounts} drill={drill} explain={explain} />
      ) : tab === "balance_sheet" ? (
        <BalanceSheetView data={data} accounts={groupAccounts} drill={drill} explain={explain} openProfit={openProfit} />
      ) : tab === "cash_flow" ? (
        <CashFlowView data={data} />
      ) : (
        <CashBankView data={data} drill={drill} explain={explain} />
      )}
      <AccountLedgerDrawer target={drillTarget} companyId={effectiveCompanyId} onClose={() => setDrillTarget(null)} />
    </div>
  );
}

function TrialBalanceView({ data, accounts, drill, explain }) {
  const rows = withGroups(data.rows, accounts, ["debit", "credit"]);
  return (
    <div className="rounded-xl border border-border bg-card" data-testid="trial-balance-view">
      {!data.balanced && (
        <div className="border-b border-border bg-rose-500/10 px-4 py-2 text-xs font-medium text-rose-500" data-testid="trial-balance-unbalanced-warning">
          Trial balance does not balance — please investigate before relying on this report.
        </div>
      )}
      <Table>
        <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Account</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={`${r.is_group ? "g" : "a"}-${r.account_code}`} className={r.is_group ? "bg-muted/40 font-semibold" : ""}>
              <TableCell className="font-mono text-xs">{r.account_code}</TableCell>
              <TableCell className="text-foreground" style={{ paddingLeft: `${(r.level || 0) * 18 + 16}px` }}>{r.account_name}<AccountNote row={{ ...r, amount: 0 }} explain={explain} /></TableCell>
              <TableCell className="text-xs text-muted-foreground">{r.account_type}</TableCell>
              <TableCell className="text-right">{Number(r.debit) ? <Amt value={r.debit} row={r} drill={drill} /> : ""}</TableCell>
              <TableCell className="text-right">{Number(r.credit) ? <Amt value={r.credit} row={r} drill={drill} /> : ""}</TableCell>
            </TableRow>
          ))}
          <TableRow className="font-semibold"><TableCell colSpan={3}>Total</TableCell><TableCell className="text-right font-mono" data-testid="trial-balance-total-debit">{fmtMoney(data.total_debit)}</TableCell><TableCell className="text-right font-mono" data-testid="trial-balance-total-credit">{fmtMoney(data.total_credit)}</TableCell></TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function ProfitAndLossView({ data, accounts, drill, explain }) {
  return (
    <div className="space-y-4" data-testid="profit-and-loss-view">
      <Section title="Income" natural="credit" rows={data.income} total={data.total_income} accounts={accounts} drill={drill} explain={explain} />
      <Section title="Expense" natural="debit" rows={data.expense} total={data.total_expense} accounts={accounts} drill={drill} explain={explain} />
      <div className="rounded-xl border border-border bg-card p-4 text-right">
        <span className="text-sm text-muted-foreground">Net Profit (Income − Expense): </span>
        <span className="font-mono text-lg font-semibold text-foreground" data-testid="net-profit-value">₹{fmtMoney(data.net_profit)}</span>
      </div>
    </div>
  );
}

function BalanceSheetView({ data, accounts, drill, explain, openProfit }) {
  const sum = (rows) => rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const profit = Number(data.net_income_to_date || 0);
  const totalLiab = data.total_liabilities !== undefined ? Number(data.total_liabilities) : sum(data.liabilities);
  const totalEq = (data.total_equity !== undefined ? Number(data.total_equity) : sum(data.equity)) + profit;
  const totalAssets = Number(data.total_assets || 0);
  const right = data.total_liabilities_and_equity !== undefined ? Number(data.total_liabilities_and_equity) : totalLiab + totalEq;
  const diff = totalAssets - right;
  const fyStart = data.fy_start;
  const profitRow = { special: "profit", account_code: "", account_name: `Profit for the year so far (${fyStart ? fyStart + " to " : "till "}${data.as_of})`, amount: profit };
  return (
    <div className="space-y-4" data-testid="balance-sheet-view">
      <p className="text-xs text-muted-foreground">
        Every amount below comes from posted accounting entries up to {data.as_of}. Click any underlined amount to see the exact entries and the bill, receipt or payment each came from.
      </p>
      <Section title="Assets — what the company owns or is owed" natural="debit" rows={data.assets} total={totalAssets} totalLabel="Total Assets" accounts={accounts} drill={drill} explain={explain} />
      <Section title="Liabilities — what the company owes" natural="credit" rows={data.liabilities} total={totalLiab} totalLabel="Total Liabilities" accounts={accounts} drill={drill} explain={explain} />
      <Section title="Equity — owners' money, including profit so far" natural="credit" rows={data.equity} total={totalEq} totalLabel="Total Equity (incl. profit so far)" accounts={accounts} drill={drill} explain={explain}
        extra={[profitRow]} onSpecial={() => openProfit(fyStart || data.as_of.slice(0, 4) + "-04-01", data.as_of)} />
      <div className="rounded-xl border border-border bg-card" data-testid="balance-sheet-summary">
        <div className="border-b border-border px-4 py-2"><h3 className="text-sm font-semibold text-foreground">Does it balance?</h3></div>
        <Table>
          <TableBody>
            <TableRow><TableCell>Total Assets</TableCell><TableCell className="text-right"><Amt value={totalAssets} strong /></TableCell></TableRow>
            <TableRow><TableCell>Total Liabilities</TableCell><TableCell className="text-right"><Amt value={totalLiab} /></TableCell></TableRow>
            <TableRow><TableCell>Total Equity (incl. profit so far)</TableCell><TableCell className="text-right"><Amt value={totalEq} /></TableCell></TableRow>
            <TableRow className="font-semibold"><TableCell>Liabilities + Equity</TableCell><TableCell className="text-right" data-testid="balance-sheet-total-liab-equity"><Amt value={right} strong /></TableCell></TableRow>
            <TableRow>
              <TableCell colSpan={2}>
                {Math.abs(diff) < 0.005 ? (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400" data-testid="balance-sheet-balanced"><CheckCircle2 className="h-4 w-4" /> Balanced — Assets equal Liabilities + Equity.</span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-500" data-testid="balance-sheet-unbalanced-warning"><AlertTriangle className="h-4 w-4" /> Out of balance by ₹{fmtMoney(Math.abs(diff))} — Assets ₹{fmtMoney(totalAssets)} vs Liabilities + Equity ₹{fmtMoney(right)}. Check the entries marked "unusual" above.</span>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function CashBankView({ data, drill, explain }) {
  return (
    <div className="rounded-xl border border-border bg-card" data-testid="cash-bank-view">
      <Table>
        <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Account</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
        <TableBody>
          {data.accounts.map((r) => (
            <TableRow key={r.account_code}><TableCell className="font-mono text-xs">{r.account_code}</TableCell><TableCell className="text-foreground">{r.account_name}<AccountNote row={{ ...r, amount: 0 }} explain={explain} /></TableCell><TableCell className="text-right"><Amt value={r.balance} row={r} drill={drill} /></TableCell></TableRow>
          ))}
          <TableRow className="font-semibold"><TableCell colSpan={2}>Total</TableCell><TableCell className="text-right"><Amt value={data.total} strong /></TableCell></TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function Section({ title, rows: rawRows, total, totalLabel = "Total", accounts, natural, drill, explain, extra = [], onSpecial }) {
  const rows = [...withGroups(rawRows, accounts, ["amount"]), ...extra];
  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="border-b border-border px-4 py-2"><h3 className="text-sm font-semibold text-foreground">{title}</h3></div>
      {rows.length === 0 ? (
        <p className="px-4 py-3 text-xs text-muted-foreground">No entries.</p>
      ) : (
        <Table>
          <TableBody>
            {rows.map((r) => r.special ? (
              <TableRow key="special-profit" className="bg-muted/20">
                <TableCell className="w-24" />
                <TableCell className="text-foreground">
                  <button type="button" onClick={onSpecial} className="inline-flex items-center gap-1 text-left text-primary hover:underline" data-testid="balance-sheet-profit-link">
                    {r.account_name}<ChevronRight className="h-3.5 w-3.5" />
                  </button>
                  {explain && <div className="mt-0.5 text-[11px] text-muted-foreground">Income minus expenses since the financial year began. Click to see the Profit &amp; Loss behind it.</div>}
                </TableCell>
                <TableCell className="text-right"><Amt value={r.amount} /></TableCell>
              </TableRow>
            ) : (
              <TableRow key={`${r.is_group ? "g" : "a"}-${r.account_code}`} className={r.is_group ? "bg-muted/40 font-semibold" : ""}>
                <TableCell className="w-24 font-mono text-xs">{r.account_code}</TableCell>
                <TableCell className="text-foreground" style={{ paddingLeft: `${(r.level || 0) * 18 + 16}px` }}>{r.account_name}<AccountNote row={r} natural={natural} explain={explain} /></TableCell>
                <TableCell className="text-right align-top"><Amt value={r.amount} row={r} drill={drill} strong={r.is_group} /></TableCell>
              </TableRow>
            ))}
            {total !== undefined && (
              <TableRow className="border-t-2 border-border font-semibold"><TableCell colSpan={2}>{totalLabel}</TableCell><TableCell className="text-right"><Amt value={total} strong /></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function CashFlowView({ data }) {
  const money = (v) => `₹${fmtNum(v, 2)}`;
  return (
    <div className="space-y-4" data-testid="cash-flow-view">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[["Opening cash", data.opening], ["Net change", data.net_change], ["Closing cash", data.closing]].map(([k, v]) => (
          <div key={k} className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{k}</p>
            <p className={`mt-2 font-mono text-lg font-bold ${k === "Net change" && Number(v) < 0 ? "text-rose-500" : "text-foreground"}`}>{money(v)}</p>
          </div>
        ))}
        <div className="rounded-xl border border-border bg-card p-4 text-xs text-muted-foreground">
          <p className="font-semibold uppercase tracking-wider">Cash accounts</p>
          <p className="mt-2">{data.cash_accounts.join(", ")}</p>
          <p className={`mt-1 font-medium ${data.check_ok ? "text-emerald-600 dark:text-emerald-400" : "text-rose-500"}`}>{data.check_ok ? "Opening + change = closing ✓" : "Opening + change ≠ closing — check"}</p>
        </div>
      </div>
      {data.sections.map((sec) => (
        <div key={sec.key} className="rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-4 py-2">
            <h3 className="text-sm font-semibold text-foreground">{sec.label}</h3>
            <span className={`font-mono text-sm font-semibold ${Number(sec.total) < 0 ? "text-rose-500" : "text-emerald-600 dark:text-emerald-400"}`}>{money(sec.total)}</span>
          </div>
          {sec.rows.length === 0 ? <p className="px-4 py-3 text-xs text-muted-foreground">No movement in this period.</p> : (
            <Table>
              <TableHeader><TableRow><TableHead>Line</TableHead><TableHead className="text-right">Money in</TableHead><TableHead className="text-right">Money out</TableHead><TableHead className="text-right">Net</TableHead></TableRow></TableHeader>
              <TableBody>
                {sec.rows.map((r) => (
                  <TableRow key={`${r.account_code}-${r.label}`}>
                    <TableCell className="text-foreground">{r.label} <span className="font-mono text-[10px] text-muted-foreground">{r.account_code}</span></TableCell>
                    <TableCell className="text-right font-mono text-xs">{Number(r.inflow) ? fmtNum(r.inflow, 2) : ""}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{Number(r.outflow) ? fmtNum(r.outflow, 2) : ""}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold">{fmtNum(r.net, 2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      ))}
    </div>
  );
}
